import { useEffect, useRef, useState } from "react";
import { wsUrl } from "../lib/api";

/**
 * Listens on the live's chat WebSocket for `sold_event` frames broadcast by the
 * backend after a payment is confirmed. Renders each event as a stacked green
 * "SOLD" flash toast in the bottom-left corner of the screen. Toasts auto-dismiss
 * after 4 seconds. This is intentionally its OWN socket (separate from the main
 * ChatPanel one) so the same WS handshake works even if the chat is closed or
 * we're on the host console.
 */
export default function SoldFlashOverlay({ liveId }) {
  const [flashes, setFlashes] = useState([]);
  const wsRef = useRef(null);
  const timeoutsRef = useRef([]);

  useEffect(() => {
    if (!liveId) return;
    const ws = new WebSocket(wsUrl(`/api/ws/chat/${liveId}`));
    wsRef.current = ws;
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === "sold_event") {
          const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
          setFlashes((prev) => [...prev.slice(-2), { id, ...data }]);
          const t = setTimeout(() => {
            setFlashes((prev) => prev.filter((f) => f.id !== id));
          }, 4200);
          timeoutsRef.current.push(t);
        }
      } catch (err) { console.warn("sold-flash parse error", err); }
    };
    return () => {
      try { ws.close(); } catch (err) { console.warn("sold-flash close error", err); }
      timeoutsRef.current.forEach((t) => clearTimeout(t));
      timeoutsRef.current = [];
    };
  }, [liveId]);

  if (flashes.length === 0) return null;

  return (
    <div
      className="fixed z-[60] left-3 pointer-events-none flex flex-col-reverse gap-2"
      style={{ bottom: "calc(env(safe-area-inset-bottom) + 88px)" }}
      data-testid="sold-flash-overlay"
    >
      {flashes.map((f) => (
        <SoldFlashCard key={f.id} flash={f} />
      ))}
    </div>
  );
}

function SoldFlashCard({ flash }) {
  return (
    <div
      className="border-2 border-[#7ED957] bg-[#FFFFFF] pl-2 pr-3 py-2 flex items-center gap-2.5 shadow-[0_8px_24px_rgba(22,163,74,0.25)] animate-sold-in"
      data-testid="sold-flash-card"
    >
      <div className="w-8 h-8 bg-[#7ED957] text-[#161616] font-display text-lg flex items-center justify-center flex-shrink-0">
        {flash.buyer_initial || "★"}
      </div>
      <div className="min-w-0">
        <div className="font-mono-ds text-[9px] uppercase tracking-widest text-[#7ED957] font-bold leading-none">
          ● SOLD
        </div>
        <div className="font-display text-[13px] uppercase tracking-tight text-[#161616] leading-tight mt-0.5 truncate max-w-[220px]">
          {flash.quantity > 1 ? `${flash.quantity} × ` : ""}{flash.product_name}
        </div>
        <div className="font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E] tabular-nums leading-none mt-0.5 flex items-center gap-1.5">
          <span>₹{Number(flash.price || 0).toLocaleString("en-IN")}</span>
          {flash.ledger_no != null && (
            <>
              <span className="opacity-40">·</span>
              <span className="text-[#264733] font-bold" data-testid="sold-flash-ledger">
                Ledger No. {String(flash.ledger_no).padStart(3, "0")}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
